import React from 'react';
import { RhythmPatternStrip } from './RhythmPatternStrip';
import { GeometryCanvas } from '@interactive-room';
import {
  PrintEarTrainingBlock,
  PrintMelodyStaffBlock,
  PrintRhythmCanvasBlock,
  PrintSymbolAnalysisBlock,
  PrintAnatomiaBlock,
  PrintDictadoRhythmBlock,
  PrintVexRhythmMeasureBlock,
  PrintFigureLegend,
} from './PrintMusicNativeBlocks';
import { PrintMathWorkspace } from './PrintMathWorkspace';
import { PrintMeasurementTable } from './PrintMeasurementTable';
import {
  MEASUREMENT_TABLE_PURPOSES,
  resolveBlockBlueprintPurpose,
  resolveDesarrolloPrintLayout,
  resolveDesarrolloWritingLineCount,
} from './printDesarrolloLayout';
import {
  EXAM_BLOCK_LABELS,
  GAME_MODE_CATALOG,
  FIGURE_SYMBOL,
  inferFigureFromSyllable,
  sanitizeRhythmItem,
  isListenPrompt,
  humanizeMusicText,
  cleanCompletarPrintTitle,
  parseCompletarPrintSegments,
  extractSimulationMatrix,
  resolveSimulationMatrixForSandbox,
  resolveSequencerPresentation,
  sequencerOrderInstruction,
  sequencerStepMarker,
  sanitizeSequenceItems,
  type EvaluationBlock,
  type GameMode,
  type SandboxPayload,
  parseInlineMarkdownSegments,
  extractNewsComparisonFromMixedText,
  parseStructuredNewspaperArticle,
  entriesToContrastRows,
  FormulaDestacada,
  MathEquationView,
  NumberLineView,
  CountingCollectionView,
  DataChartView,
  FractionModelView,
  PercentGridView,
  CartesianPlaneView,
  COUNTING_ITEM_TYPES,
  type CountingItemType,
  enforceHumanitiesGuideBlocks,
  isHumanitiesFormulaSubject,
  resolveFiguraStudentPlaceholder,
} from '@interactive-room';
import { PrintExploraSimuladorBlock } from './PrintExploraSimuladorBlock';
import { PrintGeometryCanvasBlock } from './PrintGeometryBlocks';
import { PrintRichText } from './PrintRichText';
import { PrintStimulusWrapper } from './PrintStimulusWrapper';
import { PrintThemedReading } from './PrintThemedReading';
import {
  PrintEditorialContrastTable,
  PrintNewsComparison,
  PrintPairedTerms,
  PrintStructuredNewspaper,
} from './PrintNewsEditorial';
import { PrintSourceAnalysis } from './PrintSourceAnalysis';
import { PrintStructuralBreakdown } from './PrintStructuralBreakdown';
import { PrintContextImage } from './PrintContextImage';
import { PrintSurveyTable } from './PrintSurveyTable';
import { PrintSurveyChart } from './PrintSurveyChart';
import { PrintSurveyQuestionForm } from './PrintSurveyQuestionForm';
import { PrintSurveyExampleCallout } from './PrintSurveyExampleCallout';
import { PrintSurveyResponseForm } from './PrintSurveyResponseForm';
import { resolveSurveyResponsePlan } from './printSurveyResponseTemplates';
import {
  isSurveyCompletarPayload,
  isSurveyDesarrolloBlock,
  resolveSurveyBlockTitle,
  resolveSurveyChartPrintConfig,
  resolveSurveyTableConfig,
  resolveSanitizedPrintStimulus,
  shouldEmbedSurveyStimulusInBody,
  normalizeSurveyInstruction,
  isUnfitSurveyStudentLabel,
} from './printSurveyUtils';
import { resolvePrintStimulus } from '@interactive-room';
import { EditableBlockWrapper } from '../../components/ui/EditableBlockWrapper';
import {
  type PrintBlockRole,
} from './printBlockRole';
import type { ExamBlockExamContext } from '../components/ExamBlockInlineEditor';
import { PrintEditableText, PrintEditableTitle } from './PrintEditableText';
import { usePrintInlineEdit } from './PrintInlineEditContext';

const MUSIC_ONLY_PRINT_MODES = new Set<GameMode>([
  'canvas_breakdown',
  'rhythm_canvas',
  'symbol_analysis',
  'anatomia',
  'melody_staff',
  'ear_training',
  'dictado',
]);

function isMusicaSubjectTag(subjectTag?: string): boolean {
  return String(subjectTag || '').toLowerCase() === 'musica';
}

function isMusicOnlyPrintMode(mode: GameMode, subjectTag?: string): boolean {
  return !isMusicaSubjectTag(subjectTag) && MUSIC_ONLY_PRINT_MODES.has(mode);
}

function isTechnicalStudentLabel(text: string): boolean {
  const value = String(text || '').trim();
  if (!value) return true;
  if (isUnfitSurveyStudentLabel(value)) return true;
  return /colecci[oó]n contable|recta num[eé]rica|motor de ecuaciones|plano geom[eé]trico|fase (pict[oó]rica|transici[oó]n|simb[oó]lica)|counting_collection|number_line|math_equation|info_block|destacado|contexto \/|pr[aá]ctica guiada|^OA \d+/i.test(
    value,
  );
}

function normalizeCountingItemType(raw: unknown): CountingItemType {
  const key = String(raw || 'star').toLowerCase();
  return COUNTING_ITEM_TYPES.includes(key as CountingItemType) ? (key as CountingItemType) : 'star';
}

function PrintMusicGlyphs({ text }: { text: string }) {
  const hasGlyphs = /[♩♫♪♬𝅗𝅥𝅝𝄽]/.test(text);
  return (
    <span className={hasGlyphs ? 'print-music-glyphs' : undefined}>{text}</span>
  );
}

function blockTitle(block: EvaluationBlock): string {
  const payload = block.payload as Record<string, unknown>;
  const surveyTitle = resolveSurveyBlockTitle(block);
  if (surveyTitle) return surveyTitle;

  if (block.mode === 'completar') {
    const printTitle = payload.printTitle ? String(payload.printTitle) : null;
    if (printTitle && !isTechnicalStudentLabel(printTitle)) {
      return cleanCompletarPrintTitle(printTitle);
    }
    const candidate = String(block.label || payload.title || '');
    if (candidate && !isTechnicalStudentLabel(candidate)) {
      return cleanCompletarPrintTitle(candidate);
    }
    return cleanCompletarPrintTitle(String(payload.instruction || payload.prompt || 'Completa'));
  }
  if (block.mode === 'sandbox') {
    const matrix = extractSimulationMatrix(payload);
    if (matrix?.title && !isTechnicalStudentLabel(String(matrix.title))) return matrix.title;
  }

  const candidates = [
    block.label,
    payload.title,
    payload.sectionTitle,
    payload.instruction,
    payload.prompt,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (!isTechnicalStudentLabel(candidate)) return candidate;
  }
  return '';
}

function PrintTeacherGuide({ script }: { script: string }) {
  return (
    <div className="print-teacher-guide" role="note">
      <p className="print-teacher-guide__label">🗣️ Guía del profesor</p>
      <p className="print-teacher-guide__body">{script}</p>
    </div>
  );
}

function PrintArenaBlock({ payload }: { payload: Record<string, unknown> }) {
  const edit = usePrintInlineEdit();
  const questions = Array.isArray(payload.questions) ? payload.questions : [];
  const blockTeacherScript = payload.teacherScript ? String(payload.teacherScript) : null;

  const commitQuestion = (qi: number, patch: Record<string, unknown>) => {
    if (!edit) return;
    const next = questions.map((raw, index) =>
      index === qi ? { ...(raw as Record<string, unknown>), ...patch } : raw,
    );
    edit.patchPayload({ questions: next });
  };

  return (
    <div className="print-block__body">
      {blockTeacherScript && <PrintTeacherGuide script={blockTeacherScript} />}
      {questions.map((raw, qi) => {
        const q = raw as Record<string, unknown>;
        const teacherScript = q.teacherScript ? String(q.teacherScript) : null;
        let prompt = String(q.studentPrompt || q.prompt || `Pregunta ${qi + 1}`);
        if (isListenPrompt(prompt) && teacherScript) {
          prompt = 'Transcribe el patrón rítmico que percibes:';
        }
        prompt = humanizeMusicText(prompt);
        const kind = String(q.kind || 'multiple-choice');
        const options = Array.isArray(q.options) ? q.options.map(String) : [];
        const rhythmPattern = q.rhythmPattern as Record<string, unknown> | undefined;
        const promptField = q.studentPrompt != null ? 'studentPrompt' : 'prompt';

        return (
          <div key={String(q.id || qi)} className="print-arena-q">
            {teacherScript && <PrintTeacherGuide script={teacherScript} />}
            {(rhythmPattern || q.patternTokens) && (
              <RhythmPatternStrip
                rhythmPattern={rhythmPattern as never}
                patternTokens={q.patternTokens as string[] | undefined}
                rhythmMethod={payload.rhythmMethod as never}
                graphicMode={payload.graphicMode as never}
                showSlots={false}
              />
            )}
            <p className="print-arena-q__prompt">
              <strong>{qi + 1}.</strong>{' '}
              <PrintEditableText
                value={prompt}
                as="span"
                aria-label={`Pregunta ${qi + 1}`}
                onCommit={(next) => commitQuestion(qi, { [promptField]: next })}
              />
            </p>
            {teacherScript ? (
              <div className="print-writing-lines print-writing-lines--compact" aria-hidden>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="print-writing-line" />
                ))}
              </div>
            ) : kind === 'true-false' ? (
              <ul className="print-mc-options">
                <li><span className="print-mc-box" /> Verdadero</li>
                <li><span className="print-mc-box" /> Falso</li>
              </ul>
            ) : kind === 'ordering' ? (
              <ol className="print-order-list">
                {(Array.isArray(q.items) ? q.items.map(String) : []).map((item, i) => (
                  <li key={i}>
                    <PrintEditableText
                      value={item}
                      as="span"
                      aria-label={`Ítem ${i + 1}`}
                      onCommit={(next) => {
                        const items = (Array.isArray(q.items) ? q.items.map(String) : []).map((v, idx) =>
                          idx === i ? next : v,
                        );
                        commitQuestion(qi, { items });
                      }}
                    />
                  </li>
                ))}
              </ol>
            ) : (
              <ul className="print-mc-options">
                {options.map((opt, oi) => (
                  <li key={oi}>
                    <span className="print-mc-box" />
                    <span className="print-mc-letter">{String.fromCharCode(65 + oi)}.</span>{' '}
                    <PrintEditableText
                      value={opt}
                      as="span"
                      aria-label={`Opción ${String.fromCharCode(65 + oi)}`}
                      onCommit={(next) => {
                        const nextOptions = options.map((v, idx) => (idx === oi ? next : v));
                        commitQuestion(qi, { options: nextOptions });
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface FractionFormulaConfig {
  numeratorLabel: string;
  denominatorLabel: string;
  suffix: string;
}

function resolvePercentFormula(payload: Record<string, unknown>): FractionFormulaConfig | null {
  if (payload.formulaLayout !== 'fraction-percent') return null;
  return {
    numeratorLabel: String(payload.formulaNumeratorLabel || 'Valor Final − Valor Inicial'),
    denominatorLabel: String(payload.formulaDenominatorLabel || 'Valor Inicial'),
    suffix: String(payload.formulaSuffix || '× 100'),
  };
}

function resolveCompletarFraction(payload: Record<string, unknown>): FractionFormulaConfig | null {
  return resolvePercentFormula(payload);
}

function PrintCompletarFraction({ config }: { config: FractionFormulaConfig }) {
  return (
    <div className="print-completar-fraction" role="math" aria-label="Fórmula fraccionaria para completar">
      <div className="print-completar-fraction__stack">
        <div className="print-completar-fraction__part print-completar-fraction__part--num">
          {config.numeratorLabel && (
            <span className="print-completar-fraction__hint">{config.numeratorLabel}</span>
          )}
          <span className="print-completar-fraction__blank" aria-hidden>
            ................................
          </span>
        </div>
        <div className="print-completar-fraction__bar" aria-hidden />
        <div className="print-completar-fraction__part print-completar-fraction__part--den">
          {config.denominatorLabel && (
            <span className="print-completar-fraction__hint">{config.denominatorLabel}</span>
          )}
          <span className="print-completar-fraction__blank" aria-hidden>
            ................................
          </span>
        </div>
      </div>
      <span className="print-completar-fraction__suffix">{config.suffix}</span>
    </div>
  );
}

function PrintInlineMarkdownText({ text }: { text: string }) {
  return (
    <>
      {parseInlineMarkdownSegments(text).map((segment, index) =>
        segment.bold ? (
          <strong key={index}>{segment.text}</strong>
        ) : (
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        ),
      )}
    </>
  );
}

function PrintCompletarBlock({
  payload,
  purpose = '',
}: {
  payload: Record<string, unknown>;
  purpose?: string;
}) {
  const edit = usePrintInlineEdit();
  const text = String(payload.text || '');

  if (edit?.isEditing) {
    return (
      <div className="print-block__body">
        <PrintEditableText
          value={text}
          multiline
          as="div"
          className="print-completar-text"
          placeholder="Texto con huecos [palabra]…"
          aria-label="Texto del completar"
          onCommit={(next) => edit.patchPayload({ text: next })}
        />
      </div>
    );
  }

  const fraction = resolveCompletarFraction(payload);
  const segments = parseCompletarPrintSegments(text);
  const rhythmPattern = payload.rhythmPattern as Record<string, unknown> | undefined;
  const hasRhythmStrip = Boolean(rhythmPattern || payload.patternTokens);
  const hasBlanks = segments.some((s) => s.kind === 'blank');
  const geometry = payload.geometry as import('@interactive-room').GeometryPayload | undefined;
  const showSurveyTable = isSurveyCompletarPayload(payload);

  if (showSurveyTable) {
    const tableConfig = resolveSurveyTableConfig(payload, purpose);
    const instruction =
      String(payload.text || '')
        .replace(/\|[^|]+\|/g, '')
        .replace(/\[[^\]]+\]/g, '')
        .trim() || undefined;

    return (
      <div className="print-block__body">
        <PrintSurveyTable payload={payload} config={tableConfig} instruction={instruction} />
      </div>
    );
  }

  // Texto completo de comparación / noticia estructurada (con o sin blancos).
  const mixedComparison = extractNewsComparisonFromMixedText(text);
  if (mixedComparison) {
    const restForBlanks = [mixedComparison.instruction, mixedComparison.trailing]
      .filter(Boolean)
      .join('\n\n');
    const blankSegments = hasBlanks ? parseCompletarPrintSegments(restForBlanks || text) : [];
    const restHasBlanks = blankSegments.some((s) => s.kind === 'blank');

    return (
      <div className="print-block__body">
        {mixedComparison.instruction ? (
          <div className="print-completar-instruction">
            <PrintRichText text={mixedComparison.instruction} />
          </div>
        ) : null}
        <PrintNewsComparison bundle={mixedComparison.bundle} />
        {restHasBlanks ? (
          <p className="print-completar-text">
            {blankSegments.map((segment, i) =>
              segment.kind === 'blank' ? (
                <span key={i} className="print-inline-blank" aria-hidden />
              ) : (
                <span key={i}>
                  <PrintInlineMarkdownText text={segment.value} />
                </span>
              ),
            )}
          </p>
        ) : null}
      </div>
    );
  }
  const structuredNews = !hasBlanks ? parseStructuredNewspaperArticle(text) : null;
  if (structuredNews) {
    return (
      <div className="print-block__body">
        <PrintStructuredNewspaper article={structuredNews} />
      </div>
    );
  }

  return (
    <div className="print-block__body">
      {geometry?.shapeType && (
        <div className="print-geometry-canvas print-completar-geometry">
          <GeometryCanvas geometry={geometry} width={320} height={220} printMode />
        </div>
      )}
      {hasRhythmStrip && (
        <RhythmPatternStrip
          rhythmPattern={rhythmPattern as never}
          patternTokens={
            (payload.originalBarTokens as string[] | undefined) ??
            (payload.patternTokens as string[] | undefined)
          }
          timeSignature={payload.timeSignature as never}
          meter={payload.meter as string | undefined}
          targetBarNumber={Number(payload.targetBarNumber ?? payload.barIndex) || undefined}
          dottedIndices={
            Array.isArray(payload.rhythmDots)
              ? (payload.rhythmDots as number[])
              : Array.isArray(payload.dottedIndices)
                ? (payload.dottedIndices as number[])
                : []
          }
          rhythmMethod={payload.rhythmMethod as never}
          graphicMode={payload.graphicMode as never}
          showSlots={false}
          slotMode="missing"
        />
      )}
      {fraction && <PrintCompletarFraction config={fraction} />}
      {hasBlanks && !hasRhythmStrip && (
        <p className="print-completar-text">
          {segments.map((segment, i) =>
            segment.kind === 'blank' ? (
              <span key={i} className="print-inline-blank" aria-hidden />
            ) : (
              <span key={i}>
                <PrintInlineMarkdownText text={segment.value} />
              </span>
            ),
          )}
        </p>
      )}
      {!hasBlanks && !hasRhythmStrip && !fraction && text.trim() && (
        <div className="print-completar-text">
          <PrintRichText text={text} />
        </div>
      )}
      {hasBlanks && hasRhythmStrip && (
        <p className="print-staff-answer-line">
          Figura faltante:{' '}
          <span className="print-staff-blank" aria-hidden>
            ________________
          </span>
        </p>
      )}
    </div>
  );
}

function PrintVinculaBlock({ payload }: { payload: Record<string, unknown> }) {
  const edit = usePrintInlineEdit();
  const pairs = Array.isArray(payload.pairs)
    ? (payload.pairs as Array<{ id?: string; ladoA?: string; ladoB?: string }>)
    : [];
  const rightShuffled = [...pairs].sort((a, b) => String(a.ladoB).localeCompare(String(b.ladoB), 'es'));

  const commitPair = (pairId: string | undefined, index: number, patch: { ladoA?: string; ladoB?: string }) => {
    if (!edit) return;
    const next = pairs.map((pair, i) => {
      const match = pairId ? pair.id === pairId : i === index;
      return match ? { ...pair, ...patch } : pair;
    });
    edit.patchPayload({ pairs: next });
  };

  return (
    <div className="print-block__body">
      <div className="print-vincula-grid">
        <div className="print-vincula-col print-vincula-col--a">
          <p className="print-vincula-head">Columna A</p>
          <ul className="print-vincula-list print-vincula-list--a">
            {pairs.map((pair, i) => (
              <li key={pair.id || i}>
                <PrintEditableText
                  value={String(pair.ladoA || '')}
                  as="span"
                  className="print-vincula-label"
                  aria-label={`Columna A ítem ${i + 1}`}
                  onCommit={(ladoA) => commitPair(pair.id, i, { ladoA })}
                />
                <span className="print-vincula-circle" aria-hidden>○</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="print-vincula-bridge" aria-hidden />
        <div className="print-vincula-col print-vincula-col--b">
          <p className="print-vincula-head">Columna B</p>
          <ul className="print-vincula-list print-vincula-list--b">
            {rightShuffled.map((pair, i) => {
              const sourceIndex = pairs.findIndex((p) => (pair.id ? p.id === pair.id : p === pair));
              return (
                <li key={`r-${pair.id || i}`}>
                  <span className="print-vincula-circle" aria-hidden>○</span>
                  <PrintEditableText
                    value={String(pair.ladoB || '')}
                    as="span"
                    className="print-vincula-label"
                    aria-label={`Columna B ítem ${i + 1}`}
                    onCommit={(ladoB) => commitPair(pair.id, sourceIndex >= 0 ? sourceIndex : i, { ladoB })}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <p className="print-hint">Une con línea cada ○ de la columna A con su pareja en la columna B.</p>
    </div>
  );
}

function printOrderInstruction(itemCount: number, subjectTag?: string): string {
  return sequencerOrderInstruction(resolveSequencerPresentation(subjectTag), itemCount);
}

type RhythmFigure = keyof typeof FIGURE_SYMBOL;

interface ResolvedRhythmCell {
  syllable: string;
  symbol: string;
}

function resolveRhythmCells(payload: Record<string, unknown>): ResolvedRhythmCell[] | null {
  const items = Array.isArray(payload.items) ? payload.items.map(String) : [];
  const rhythmCells = Array.isArray(payload.rhythmCells) ? payload.rhythmCells : null;

  if (payload.printLayout === 'rhythm-bar' || rhythmCells?.length) {
    return items.map((item, i) => {
      const raw = rhythmCells?.[i] as { syllable?: string; figure?: string; symbol?: string } | undefined;
      const syllable = sanitizeRhythmItem(String(raw?.syllable || item));
      const figure = (raw?.figure as RhythmFigure) || inferFigureFromSyllable(syllable);
      return { syllable, symbol: String(raw?.symbol || FIGURE_SYMBOL[figure] || '♩') };
    });
  }

  const takadimiLike = items.some((item) => /ta(-|$)|takadimi|♩|♫|▰/i.test(item));
  if (!takadimiLike) return null;

  return items.map((item) => {
    const syllable = sanitizeRhythmItem(item);
    const figure = inferFigureFromSyllable(syllable);
    return { syllable, symbol: FIGURE_SYMBOL[figure] || '♩' };
  });
}

function shouldUseRhythmBar(subjectTag: string | undefined, payload: Record<string, unknown>): boolean {
  if (String(subjectTag || '').toLowerCase() !== 'musica') return false;
  const cells = resolveRhythmCells(payload);
  if (!cells?.length) return false;
  return payload.printLayout === 'rhythm-bar' || subjectTag === 'musica';
}

function PrintRhythmBarBlock({
  payload,
  subjectTag,
}: {
  payload: Record<string, unknown>;
  subjectTag?: string;
}) {
  const cells = resolveRhythmCells(payload);
  if (!cells?.length) return <PrintOrderBlock payload={payload} />;

  const meter = String(payload.meter || payload.timeSignature || '').trim();
  const barIndex = Number(payload.targetBarNumber ?? payload.barIndex) || 1;
  const instruction = printOrderInstruction(cells.length, subjectTag);
  const showLabels = payload.graphicMode === 'symbols-labels';

  if (!meter) {
    return <PrintOrderBlock payload={payload} subjectTag={subjectTag} />;
  }

  return (
    <div className="print-block__body">
      {payload.teacherScript && (
        <PrintTeacherGuide script={String(payload.teacherScript)} />
      )}
      <div className="print-rhythm-bar__header" aria-hidden>
        <span className="print-rhythm-bar__header-line" />
        <span className="print-rhythm-bar__header-label">
          COMPÁS {barIndex} ({meter})
        </span>
        <span className="print-rhythm-bar__header-line" />
      </div>
      <p className="print-instruction">{instruction}</p>
      <div className="print-rhythm-bar__row" role="list">
        {cells.map((cell, i) => (
          <div key={i} className="print-rhythm-bar__cell" role="listitem">
            <span className="print-order-slot print-rhythm-bar__slot" aria-label={`Casillero ${i + 1}`} />
            <span className="print-rhythm-bar__figure" aria-hidden>
              {cell.symbol}
            </span>
            {showLabels && (
              <span className="print-rhythm-bar__syllable">({cell.syllable})</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PrintOrderBlock({
  payload,
  subjectTag,
}: {
  payload: Record<string, unknown>;
  subjectTag?: string;
}) {
  if (shouldUseRhythmBar(subjectTag, payload)) {
    return <PrintRhythmBarBlock payload={payload} subjectTag={subjectTag} />;
  }

  const items = sanitizeSequenceItems(payload.items);
  const presentation = resolveSequencerPresentation(subjectTag);
  const instruction = printOrderInstruction(items.length || 1, subjectTag);
  const edit = usePrintInlineEdit();

  return (
    <div className="print-block__body">
      <p className="print-instruction">{instruction}</p>
      <ul className="print-order-steps">
        {items.map((item, i) => (
          <li key={i} className="print-order-step">
            <span
              className={`print-order-step__index${presentation === 'history' ? ' print-order-step__index--history' : ''}${presentation === 'music' ? ' print-order-step__index--music' : ''}`}
              aria-hidden
            >
              {sequencerStepMarker(presentation, i)}
            </span>
            <span className="print-order-slot" aria-label={`Casillero orden paso ${i + 1}`} />
            <PrintEditableText
              value={item}
              as="span"
              className="print-order-step-text"
              aria-label={`Paso ${i + 1}`}
              onCommit={(next) => {
                if (!edit) return;
                const nextItems = items.map((v, idx) => (idx === i ? next : v));
                edit.patchPayload({ items: nextItems });
              }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PrintAgrupaBlock({ payload }: { payload: Record<string, unknown> }) {
  const edit = usePrintInlineEdit();
  const categories = Array.isArray(payload.categories)
    ? (payload.categories as Array<{ id?: string; label?: string }>)
    : [];
  const items = Array.isArray(payload.items)
    ? (payload.items as Array<{ id?: string; text?: string }>)
    : [];

  return (
    <div className="print-block__body">
      <div className="print-agrupa-table">
        <div className="print-agrupa-head">
          {categories.map((cat, i) => (
            <div key={cat.id || i} className="print-agrupa-cat">
              <PrintEditableText
                value={String(cat.label || `Cat. ${i + 1}`)}
                as="span"
                aria-label={`Categoría ${i + 1}`}
                onCommit={(label) => {
                  if (!edit) return;
                  const next = categories.map((c, idx) => (idx === i ? { ...c, label } : c));
                  edit.patchPayload({ categories: next });
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <ul className="print-agrupa-items">
        {items.map((item, i) => (
          <li key={item.id || i} className="print-agrupa-item">
            <PrintEditableText
              value={String(item.text || '')}
              as="span"
              aria-label={`Ítem ${i + 1}`}
              onCommit={(text) => {
                if (!edit) return;
                const next = items.map((it, idx) => (idx === i ? { ...it, text } : it));
                edit.patchPayload({ items: next });
              }}
            />
            <span className="print-agrupa-write">Categoría: ____________</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PrintDesarrolloBlock({
  payload,
  block,
  stimulus,
  isLearningGuide = false,
}: {
  payload: Record<string, unknown>;
  block?: EvaluationBlock;
  stimulus?: { title?: string; content?: string } | null;
  isLearningGuide?: boolean;
}) {
  const edit = usePrintInlineEdit();
  // ✅ NUEVA LÓGICA DE PROTECCIÓN:
  // Si el propósito es 'conversion_inquiry', ignoramos el stimulus aunque venga lleno.
  const purpose = resolveBlockBlueprintPurpose(block, payload);
  const effectiveStimulus = purpose === 'conversion_inquiry' ? null : stimulus;

  const rawPrompt = String(payload.prompt || payload.title || '').trim();
  const prompt = humanizeMusicText(
    normalizeSurveyInstruction(rawPrompt, effectiveStimulus?.content), // Usamos effectiveStimulus
  );

  const hasSurveyTemplate =
    payload.surveyResponseTemplate != null &&
    String(payload.surveyResponseTemplate) !== 'writing_lines';
  const isSurveyDesarrollo =
    isLearningGuide &&
    block &&
    (isSurveyDesarrolloBlock(block, prompt || rawPrompt) || hasSurveyTemplate);

  const promptField = (
    <PrintEditableText
      value={prompt}
      as="p"
      className="print-instruction print-instruction--desarrollo"
      multiline
      aria-label="Consigna"
      onCommit={(next) => edit?.patchPayload({ prompt: next })}
    />
  );

  if (isSurveyDesarrollo && block) {
    const plan = resolveSurveyResponsePlan(block, prompt || rawPrompt);

    return (
      <div className="print-block__body">
        {stimulus?.content ? (
          <PrintSurveyExampleCallout content={stimulus.content} title={stimulus.title} />
        ) : null}
        {prompt ? (
          promptField
        ) : plan.template === 'question_with_options' ? (
          <p className="print-instruction print-instruction--desarrollo">
            Elige un tema de interés para el curso y escribe una pregunta clara con 4 opciones de
            respuesta. Usa palabras sencillas y evita opciones que se repitan.
          </p>
        ) : null}
        <PrintSurveyResponseForm
          plan={plan}
          optionCount={Number(payload.surveyOptionCount) || 4}
        />
      </div>
    );
  }

  const printLayout = resolveDesarrolloPrintLayout(block, payload);
  const lineCount = resolveDesarrolloWritingLineCount(purpose);

  if (printLayout === 'math_workspace') {
    return (
      <div className="print-block__body">
        {promptField}
        <PrintMathWorkspace gridType="math_5mm" heightRows={8} showResultLine />
      </div>
    );
  }

  return (
    <div className="print-block__body">
      {promptField}
      <div className="print-writing-lines" aria-hidden>
        {Array.from({ length: lineCount }).map((_, i) => (
          <div key={i} className="print-writing-line" />
        ))}
      </div>
    </div>
  );
}

function PrintDictadoBlock({
  payload,
  subjectTag,
}: {
  payload: Record<string, unknown>;
  subjectTag?: string;
}) {
  const tokens = Array.isArray(payload.patternTokens) ? payload.patternTokens : [];
  if (isMusicaSubjectTag(subjectTag) && tokens.length > 0) {
    return <PrintDictadoRhythmBlock payload={payload} />;
  }

  const studentPrompt = String(payload.studentPrompt || payload.prompt || 'Transcribe el patrón rítmico:');
  const teacherScript = String(payload.teacherScript || '');
  const lines = Number(payload.transcriptionLines) || 4;

  return (
    <div className="print-block__body">
      {teacherScript && <PrintTeacherGuide script={teacherScript} />}
      <p className="print-instruction">{studentPrompt}</p>
      <div className="print-writing-lines" aria-hidden>
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="print-writing-line" />
        ))}
      </div>
    </div>
  );
}

function PrintSandboxBlock({
  payload,
  subjectTag,
}: {
  payload: Record<string, unknown>;
  subjectTag?: string;
}) {
  let matrix = null;
  try {
    matrix = resolveSimulationMatrixForSandbox(
      payload as SandboxPayload,
      String(payload.title || payload.label || ''),
      subjectTag ?? null,
    );
  } catch {
    matrix = extractSimulationMatrix(payload);
  }
  if (matrix) {
    return (
      <div className="print-block__body">
        <PrintExploraSimuladorBlock payload={matrix} />
      </div>
    );
  }
  return (
    <PrintGenericBlock mode="sandbox" payload={payload} />
  );
}

function PrintGenericBlock({ mode, payload }: { mode: GameMode; payload: Record<string, unknown> }) {
  const edit = usePrintInlineEdit();
  const title = String(payload.title || GAME_MODE_CATALOG[mode]?.displayName || mode);
  const extra = mode === 'point' ? String(payload.prompt || '') : '';

  return (
    <div className="print-block__body">
      <p className="print-instruction">
        <PrintEditableText
          value={title}
          as="span"
          aria-label="Título del bloque"
          onCommit={(next) => edit?.patchPayload({ title: next })}
        />
        {mode === 'point' ? (
          <>
            {' — '}
            <PrintEditableText
              value={extra}
              as="span"
              aria-label="Consigna"
              onCommit={(next) => edit?.patchPayload({ prompt: next })}
            />
          </>
        ) : null}
      </p>
      <div className="print-generic-box">
        <p>Espacio de trabajo</p>
      </div>
    </div>
  );
}

function PrintInfoBlock({ payload }: { payload: Record<string, unknown> }) {
  const edit = usePrintInlineEdit();
  const sectionTitle = String(payload.sectionTitle || payload.title || '');
  const rawBody = String(payload.bodyText || '');
  const bodyText = stripEchoTitle(rawBody, String(payload.title || ''));
  const imageUrl = String(payload.imageUrl || '').trim() || undefined;
  const formula = resolvePercentFormula(payload);
  const figureLegend = Array.isArray(payload.figureLegend)
    ? payload.figureLegend.map(String).filter(Boolean)
    : [];
  const figureLegendEntries = Array.isArray(payload.figureLegendEntries)
    ? (payload.figureLegendEntries as import('@interactive-room').FigureLegendEntry[])
    : undefined;
  const teacherScript = payload.teacherScript ? String(payload.teacherScript) : null;

  return (
      <div className={`print-block__body print-info-block${imageUrl ? ' print-info-block--with-image' : ''}`}>
      {teacherScript && <PrintTeacherGuide script={teacherScript} />}
      {sectionTitle && sectionTitle !== String(payload.title || '') && (
        <PrintEditableText
          value={sectionTitle}
          as="p"
          className="print-info-block__section"
          aria-label="Subtítulo de sección"
          onCommit={(next) => edit?.patchPayload({ sectionTitle: next })}
        />
      )}
      <div className="print-info-block__split">
        {imageUrl ? (
          <div className="print-info-block__media">
            <PrintContextImage
              imageUrl={imageUrl}
              alt={sectionTitle || String(payload.title || '')}
              column
            />
          </div>
        ) : null}
        <div className="print-info-block__text">
          <div className="print-info-block__body">
            {edit?.isEditing ? (
              <PrintEditableText
                value={rawBody}
                multiline
                as="div"
                aria-label="Cuerpo del texto"
                onCommit={(next) => edit.patchPayload({ bodyText: next })}
              />
            ) : (
              (() => {
                const comparison = extractNewsComparisonFromMixedText(bodyText);
                if (comparison) {
                  return (
                    <>
                      {comparison.instruction ? <PrintRichText text={comparison.instruction} /> : null}
                      <PrintNewsComparison bundle={comparison.bundle} />
                    </>
                  );
                }
                const structured = parseStructuredNewspaperArticle(bodyText);
                if (structured) return <PrintStructuredNewspaper article={structured} />;
                return <PrintRichText text={bodyText} />;
              })()
            )}
          </div>
          {formula && (
            <div className="print-info-block__formula">
              <p className="print-info-block__formula-label">Fórmula de variación porcentual</p>
              <PrintCompletarFraction config={formula} />
            </div>
          )}
          <PrintFigureLegend items={figureLegend} entries={figureLegendEntries} />
        </div>
      </div>
    </div>
  );
}

function PrintCanvasBreakdown({
  payload,
  subjectTag,
}: {
  payload: Record<string, unknown>;
  subjectTag?: string;
}) {
  const bodyText = String(payload.bodyText || '');
  const figureLegend = Array.isArray(payload.figureLegend)
    ? payload.figureLegend.map(String).filter(Boolean)
    : [];
  const figureLegendEntries = Array.isArray(payload.figureLegendEntries)
    ? (payload.figureLegendEntries as import('@interactive-room').FigureLegendEntry[])
    : undefined;
  const teacherScript = payload.teacherScript ? String(payload.teacherScript) : null;
  const showStaff =
    isMusicaSubjectTag(subjectTag) &&
    Array.isArray(payload.patternTokens) &&
    payload.patternTokens.length > 0;

  return (
    <div className="print-block__body print-canvas-breakdown">
      {teacherScript && <PrintTeacherGuide script={teacherScript} />}
      <div className="print-canvas-breakdown__body">{bodyText}</div>
      <PrintFigureLegend items={figureLegend} entries={figureLegendEntries} />
      {showStaff && (
        <PrintVexRhythmMeasureBlock
          payload={payload}
          barCount={
            Array.isArray(payload.phraseBarNumbers)
              ? (payload.phraseBarNumbers as number[]).length
              : 1
          }
        />
      )}
    </div>
  );
}

function PrintGlosarioTablaBlock({ payload }: { payload: Record<string, unknown> }) {
  const entries = Array.isArray(payload.entries)
    ? (payload.entries as Array<{ term?: string; definition?: string }>)
    : [];
  const title = String(payload.title || 'Conceptos clave');
  const leftHeader = String(payload.leftHeader || payload.columnA || 'Noticia 1');
  const rightHeader = String(payload.rightHeader || payload.columnB || 'Noticia 2');
  const isComparison =
    /comparaci[oó]n|contraste|versus|vs\.?/i.test(title) ||
    String(payload.layout || '') === 'editorial_contrast' ||
    entries.some((e) => /\s*[|/–—]\s*/.test(String(e.definition || '')));

  const contrastRows = isComparison ? entriesToContrastRows(entries) : [];
  const pairedFromContrast = contrastRows
    .filter((r) => r.left && r.right && r.left !== r.right)
    .map((r) => ({ left: r.left, right: r.right }));

  if (isComparison && contrastRows.length >= 2) {
    return (
      <div className="print-block__body print-glosario-tabla print-glosario-tabla--editorial">
        <h3 className="print-glosario-tabla__title">{title}</h3>
        {pairedFromContrast.length >= 2 ? (
          <PrintPairedTerms
            pairs={pairedFromContrast}
            leftHead={leftHeader}
            rightHead={rightHeader}
          />
        ) : null}
        <PrintEditorialContrastTable
          rows={contrastRows}
          leftHeader={leftHeader}
          rightHeader={rightHeader}
        />
      </div>
    );
  }

  return (
    <div className="print-block__body print-glosario-tabla print-glosario-tabla--editorial">
      <h3 className="print-glosario-tabla__title">{title}</h3>
      <table className="print-editorial-table print-editorial-table--glossary">
        <thead>
          <tr>
            <th scope="col">Término</th>
            <th scope="col">Definición</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={String(entry.term)}>
              <th scope="row">{String(entry.term || '')}</th>
              <td>{String(entry.definition || '')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PrintFiguraBlock({ payload }: { payload: Record<string, unknown> }) {
  const imageUrl = String(payload.imageUrl || '').trim() || undefined;
  const placeholderText = resolveFiguraStudentPlaceholder(payload);
  const caption = String(payload.caption || '').trim();

  return (
    <figure className="print-block__body print-figura-block">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={placeholderText}
          className="print-figura-block__image block h-auto w-full max-h-72 rounded-xl border border-slate-200 object-cover shadow-sm"
        />
      ) : (
        <div className="print-figura-block__placeholder">
          <span aria-hidden>🖼</span>
          <p>{placeholderText}</p>
        </div>
      )}
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function stripEchoTitle(body: string, title: string): string {
  const t = String(title || '').trim();
  let next = String(body || '').trim();
  if (!t) return next;
  if (next.toLowerCase() === t.toLowerCase()) return '';
  const escaped = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  next = next
    .replace(new RegExp(`^(?:\\*\\*)?${escaped}(?:\\*\\*)?\\s*:?[ \\t]*\\n+`, 'i'), '')
    .replace(new RegExp(`^###\\s*${escaped}\\s*\\n+`, 'i'), '')
    .trim();
  if (next.toLowerCase() === t.toLowerCase()) return '';
  return next;
}

function PrintDestacadoBlock({
  payload,
  sectionTitle = '',
}: {
  payload: Record<string, unknown>;
  sectionTitle?: string;
}) {
  const title = String(payload.title || '');
  const body = stripEchoTitle(String(payload.body || ''), title);
  const editorialTheme = String(payload.editorialTheme || '').trim() as
    | 'newspaper'
    | 'letter'
    | 'poem'
    | 'document'
    | 'prose'
    | '';
  const tone = String(payload.tone || 'neutral');
  const useTheme = editorialTheme && editorialTheme !== 'prose';
  const titleShownInSection =
    Boolean(sectionTitle.trim()) &&
    sectionTitle.trim().toLowerCase() === title.trim().toLowerCase();
  const showInnerTitle = Boolean(title.trim()) && !titleShownInSection;
  const readingTitle = titleShownInSection ? undefined : title || undefined;

  const comparison = extractNewsComparisonFromMixedText(body);
  if (comparison) {
    return (
      <aside className="print-block__body print-destacado print-destacado--neutral print-destacado--themed print-destacado--newspaper">
        {showInnerTitle ? <p className="print-destacado__title">{title}</p> : null}
        {comparison.instruction ? <PrintRichText text={comparison.instruction} /> : null}
        <PrintNewsComparison bundle={comparison.bundle} />
      </aside>
    );
  }

  if (useTheme) {
    return (
      <aside
        className={`print-block__body print-destacado print-destacado--neutral print-destacado--themed print-destacado--${editorialTheme}`}
      >
        <PrintThemedReading theme={editorialTheme} title={readingTitle} content={body} />
      </aside>
    );
  }

  const structured = parseStructuredNewspaperArticle(body);
  if (structured) {
    return (
      <aside className="print-block__body print-destacado print-destacado--neutral print-destacado--themed print-destacado--newspaper">
        <PrintStructuredNewspaper
          article={
            readingTitle && !structured.outlet
              ? { ...structured, outlet: readingTitle }
              : structured
          }
        />
      </aside>
    );
  }

  return (
    <aside className={`print-block__body print-destacado print-destacado--${tone}`}>
      {showInnerTitle ? <p className="print-destacado__title">{title}</p> : null}
      <PrintRichText text={body} />
    </aside>
  );
}

function PrintFormulaDestacadaBlock({
  payload,
  subjectTag,
}: {
  payload: Record<string, unknown>;
  subjectTag?: string;
}) {
  return (
    <div className="print-block__body print-formula-destacada max-w-full overflow-hidden">
      <FormulaDestacada
        subjectTag={subjectTag}
        payload={{
          title: String(payload.title || ''),
          latex: String(payload.latex || payload.formula || ''),
          description: String(payload.description || ''),
        }}
      />
    </div>
  );
}

function PrintSubtituloBlock({ payload }: { payload: Record<string, unknown> }) {
  return (
    <div className="print-block__body print-subtitulo-block">
      <h2>{String(payload.title || '')}</h2>
      {payload.subtitle ? <p>{String(payload.subtitle)}</p> : null}
    </div>
  );
}

export function PrintBlockSection({
  block,
  index,
  subjectTag,
  isStudyGuide = false,
  isLearningGuide = false,
  blockRole = 'neutral',
  pedagogicalMoment,
  practiceNumber,
  layout = 'full',
  editable = false,
  examContext,
  onBlockChange,
}: {
  block: EvaluationBlock;
  index: number;
  subjectTag?: string;
  isStudyGuide?: boolean;
  isLearningGuide?: boolean;
  blockRole?: PrintBlockRole;
  /** Fase 7 — variación visual acotada por momento de clase. */
  pedagogicalMoment?: import('./printPedagogicalMoment').PrintPedagogicalMoment;
  practiceNumber?: number;
  layout?: 'full' | 'grid-cell';
  editable?: boolean;
  examContext?: ExamBlockExamContext;
  onBlockChange?: (block: EvaluationBlock) => void;
}) {
  const payload = (block.payload ?? {}) as Record<string, unknown>;
  const resolvedTitle = blockTitle(block);

  let body: React.ReactNode;
  if (isMusicOnlyPrintMode(block.mode, subjectTag)) {
    body = (
      <div className="print-block__body">
        <p>{String(payload.bodyText || payload.title || 'Contenido de demostración.')}</p>
      </div>
    );
  } else switch (block.mode) {
    case 'arena':
      body = <PrintArenaBlock payload={payload} />;
      break;
    case 'completar':
      body = (
        <PrintCompletarBlock
          payload={payload}
          purpose={String((block as { _blueprintPurpose?: string })._blueprintPurpose || '')}
        />
      );
      break;
    case 'vincula':
      body = <PrintVinculaBlock payload={payload} />;
      break;
    case 'order':
      body = <PrintOrderBlock payload={payload} subjectTag={subjectTag} />;
      break;
    case 'agrupa':
      body = <PrintAgrupaBlock payload={payload} />;
      break;
    case 'desarrollo':
      body = (
        <PrintDesarrolloBlock
          payload={payload}
          block={block}
          isLearningGuide={isLearningGuide}
          stimulus={
            shouldEmbedSurveyStimulusInBody(block)
              ? resolveSanitizedPrintStimulus(payload, block.mode, resolvePrintStimulus)
              : null
          }
        />
      );
      break;
    case 'dictado':
      body = <PrintDictadoBlock payload={payload} subjectTag={subjectTag} />;
      break;
    case 'anatomia':
      body = <PrintAnatomiaBlock payload={payload} />;
      break;
    case 'rhythm_canvas':
      body = <PrintRhythmCanvasBlock payload={payload} />;
      break;
    case 'info_block':
      body = <PrintInfoBlock payload={payload} />;
      break;
    case 'subtitulo':
      body = <PrintSubtituloBlock payload={payload} />;
      break;
    case 'destacado':
      body = <PrintDestacadoBlock payload={payload} sectionTitle={resolvedTitle} />;
      break;
    case 'figura':
      body = <PrintFiguraBlock payload={payload} />;
      break;
    case 'glosario_tabla':
      body = <PrintGlosarioTablaBlock payload={payload} />;
      break;
    case 'formula_destacada':
      if (isHumanitiesFormulaSubject(subjectTag)) {
        const [converted] = enforceHumanitiesGuideBlocks([block], subjectTag);
        if (converted?.mode === 'destacado') {
          body = <PrintDestacadoBlock payload={converted.payload ?? {}} sectionTitle={resolvedTitle} />;
          break;
        }
      }
      body = <PrintFormulaDestacadaBlock payload={payload} subjectTag={subjectTag} />;
      break;
    case 'canvas_breakdown':
      body = <PrintCanvasBreakdown payload={payload} subjectTag={subjectTag} />;
      break;
    case 'symbol_analysis':
      body = <PrintSymbolAnalysisBlock payload={payload} />;
      break;
    case 'geometry_canvas':
      body = <PrintGeometryCanvasBlock payload={payload} />;
      break;
    case 'math_equation':
      body = (
        <MathEquationView
          payload={{
            instruction: String(payload.instruction || ''),
            equationLatex: String(payload.equationLatex || payload.latex || ''),
            resolutionSteps: Array.isArray(payload.resolutionSteps)
              ? payload.resolutionSteps.map((step: unknown) => String(step || ''))
              : undefined,
          }}
          showResolutionSteps={false}
        />
      );
      break;
    case 'number_line':
      body = (
        <NumberLineView
          payload={{
            instruction: String(payload.instruction || ''),
            start: Number(payload.start ?? 0),
            end: Number(payload.end ?? 10),
            step: Number(payload.step ?? 1),
            missingNumbers: Array.isArray(payload.missingNumbers)
              ? payload.missingNumbers.map((value: unknown) => Number(value)).filter((value) => Number.isFinite(value))
              : [],
          }}
        />
      );
      break;
    case 'counting_collection':
      body = (
        <CountingCollectionView
          payload={{
            instruction: String(payload.instruction || 'Cuenta los grupos'),
            groups: Array.isArray(payload.groups)
              ? payload.groups.map((group, index) => {
                  const row = group as {
                    id?: string;
                    itemType?: string;
                    count?: number;
                    label?: string;
                  };
                  return {
                    id: String(row.id || `g${index + 1}`),
                    itemType: normalizeCountingItemType(row.itemType),
                    count: Number(row.count ?? 0),
                    ...(row.label ? { label: String(row.label) } : {}),
                  };
                })
              : [],
          }}
        />
      );
      break;
    case 'data_chart': {
      const chartPurpose = resolveBlockBlueprintPurpose(block, payload);
      if (MEASUREMENT_TABLE_PURPOSES.has(chartPurpose)) {
        const instructionRaw = String(payload.instruction || '').trim();
        const titleRaw = String(payload.title || resolvedTitle || '').trim();
        const instruction =
          instructionRaw &&
          instructionRaw.toLowerCase() !== titleRaw.toLowerCase()
            ? instructionRaw
            : undefined;
        body = (
          <PrintMeasurementTable
            purpose={chartPurpose}
            instruction={instruction}
          />
        );
        break;
      }
      const chartConfig = resolveSurveyChartPrintConfig(payload, chartPurpose, { isLearningGuide });
      if (chartConfig.useTemplate) {
        body = (
          <PrintSurveyChart
            instruction={chartConfig.instruction}
            xAxisLabel={chartConfig.meta.xAxisLabel}
            yAxisLabel={chartConfig.meta.yAxisLabel}
            showTitle={false}
            columnCount={chartConfig.columnCount}
            hint={chartConfig.hint}
          />
        );
      } else {
        body = (
          <DataChartView
            payload={{
              chartType:
                payload.chartType === 'bar' ||
                payload.chartType === 'pie' ||
                payload.chartType === 'line' ||
                payload.chartType === 'dot' ||
                payload.chartType === 'table'
                  ? payload.chartType
                  : 'table',
              title: String(payload.title || 'Gráfico estadístico'),
              ...(payload.xAxisLabel ? { xAxisLabel: String(payload.xAxisLabel) } : {}),
              ...(payload.yAxisLabel ? { yAxisLabel: String(payload.yAxisLabel) } : {}),
              data: Array.isArray(payload.data)
                ? payload.data
                    .map((point) => {
                      const row = point as { label?: string; value?: unknown };
                      return {
                        label: String(row.label || '').trim(),
                        value: Number(row.value),
                      };
                    })
                    .filter((point) => point.label && Number.isFinite(point.value))
                : [],
            }}
          />
        );
      }
      break;
    }
    case 'fraction_model':
      body = (
        <FractionModelView
          payload={{
            instruction: String(payload.instruction || ''),
            numerator: Number(payload.numerator ?? 0),
            denominator: Number(payload.denominator ?? 1),
            visualType: payload.visualType === 'circle' ? 'circle' : 'bar',
          }}
        />
      );
      break;
    case 'percent_grid':
      body = (
        <PercentGridView
          payload={{
            instruction: String(payload.instruction || ''),
            percentage: Number(payload.percentage ?? 0),
          }}
        />
      );
      break;
    case 'cartesian_plane':
      body = (
        <CartesianPlaneView
          payload={{
            instruction: String(payload.instruction || ''),
            quadrants: payload.quadrants === '4' ? '4' : '1',
            gridSize: Number(payload.gridSize ?? 10),
            points: Array.isArray(payload.points)
              ? payload.points
                  .map((point) => {
                    const row = point as { x?: unknown; y?: unknown; label?: string };
                    return {
                      x: Number(row.x),
                      y: Number(row.y),
                      ...(row.label ? { label: String(row.label) } : {}),
                    };
                  })
                  .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
              : undefined,
            polygons: Array.isArray(payload.polygons)
              ? payload.polygons
                  .map((polygon) => {
                    const row = polygon as {
                      color?: string;
                      vertices?: Array<{ x?: unknown; y?: unknown }>;
                    };
                    return {
                      ...(row.color ? { color: String(row.color) } : {}),
                      vertices: Array.isArray(row.vertices)
                        ? row.vertices
                            .map((vertex) => ({
                              x: Number(vertex.x),
                              y: Number(vertex.y),
                            }))
                            .filter((vertex) => Number.isFinite(vertex.x) && Number.isFinite(vertex.y))
                        : [],
                    };
                  })
                  .filter((polygon) => polygon.vertices.length >= 3)
              : undefined,
          }}
        />
      );
      break;
    case 'structural_breakdown':
      body = (
        <PrintStructuralBreakdown
          payload={{
            title: String(payload.title || ''),
            typography: payload.typography === 'sans' ? 'sans' : 'serif',
            explanation: String(payload.explanation || ''),
            whyItMatters: String(payload.whyItMatters || ''),
            lines: Array.isArray(payload.lines)
              ? payload.lines.map((line) => {
                  const row = line as { text?: string; badge?: string };
                  return {
                    text: String(row.text || ''),
                    badge: String(row.badge || ''),
                  };
                })
              : [],
          }}
        />
      );
      break;
    case 'source_analysis':
      body = (
        <PrintSourceAnalysis
          payload={{
            title: String(payload.title || ''),
            source: {
              quote: String((payload.source as { quote?: string } | undefined)?.quote || ''),
              reference: String((payload.source as { reference?: string } | undefined)?.reference || ''),
              imageUrl: String((payload.source as { imageUrl?: string } | undefined)?.imageUrl || ''),
            },
            imageUrl: String(payload.imageUrl || ''),
            questions: Array.isArray(payload.questions)
              ? payload.questions.map((q) => String(q || '')).filter(Boolean)
              : [],
          }}
        />
      );
      break;
    case 'melody_staff':
      body = <PrintMelodyStaffBlock payload={payload} />;
      break;
    case 'ear_training':
      body = <PrintEarTrainingBlock payload={payload} />;
      break;
    case 'sandbox':
      body = <PrintSandboxBlock payload={payload} subjectTag={subjectTag} />;
      break;
    default:
      body = <PrintGenericBlock mode={block.mode} payload={payload} />;
  }

  const isTeaching = isLearningGuide && blockRole === 'teaching';
  const isPractice = isLearningGuide && blockRole === 'practice';
  const displayIndex = isPractice && practiceNumber != null ? practiceNumber : index + 1;
  const momentClass =
    isLearningGuide && pedagogicalMoment ? ` print-block--moment-${pedagogicalMoment}` : '';

  return (
    <EditableBlockWrapper
      enabled={editable}
      blockLabel={blockTitle(block)}
      block={block}
      examContext={examContext}
      onChange={onBlockChange}
    >
      <section
        className={`print-block${isStudyGuide ? ' print-block--study-guide' : ''}${
          isTeaching ? ' print-block--teaching' : ''
        }${isPractice ? ' print-block--practice' : ''}${
          layout === 'grid-cell' ? ' print-block--grid-cell' : ''
        } print-block--mode-${block.mode}${momentClass}`}
      >
        {!isStudyGuide && (
          <header
            className={`print-block__head${
              isLearningGuide && isTeaching ? ' print-block__head--editorial' : ''
            }`}
          >
            {/* Fase 7: sin sellos CONTEXTO/CONCEPTO por bloque — la tipografía manda. */}
            {isLearningGuide && isTeaching ? null : (
              <span className="print-block__num">{displayIndex}</span>
            )}
            {resolvedTitle ? (
              <div>
                <PrintEditableTitle
                  value={resolvedTitle}
                  isTeaching={isTeaching}
                  isPractice={isPractice}
                />
              </div>
            ) : null}
          </header>
        )}
        <PrintStimulusWrapper
          stimulus={
            shouldEmbedSurveyStimulusInBody(block)
              ? undefined
              : resolveSanitizedPrintStimulus(payload, block.mode, resolvePrintStimulus)
          }
        >
          {body}
        </PrintStimulusWrapper>
      </section>
    </EditableBlockWrapper>
  );
}
